import React, { useCallback, useState } from 'react';
import { Upload, FileSpreadsheet, FileText, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FileUploadZoneProps {
  onFilesSelected: (xlsx: File, pdfs: File[]) => void;
  isUploading?: boolean;
  className?: string;
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({ onFilesSelected, isUploading, className }) => {
  const [xlsxFile, setXlsxFile] = useState<File | null>(null);
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const processFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const newPdfs: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.name.endsWith('.xlsx') || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        setXlsxFile(file);
      } else if (file.name.endsWith('.pdf') || file.type === 'application/pdf') {
        newPdfs.push(file);
      }
    }
    if (newPdfs.length > 0) {
      setPdfFiles(prev => [...prev, ...newPdfs]);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    processFiles(e.dataTransfer.files);
  }, [processFiles]);

  const handleSubmit = () => {
    if (xlsxFile && pdfFiles.length > 0) {
      onFilesSelected(xlsxFile, pdfFiles);
    }
  };

  const canSubmit = xlsxFile && pdfFiles.length > 0 && !isUploading;

  return (
    <div className={cn('space-y-4', className)}>
      <div
        className={cn(
          'border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200',
          dragActive
            ? 'border-primary-400 bg-primary-50'
            : 'border-gray-200 bg-gray-50/50 hover:border-gray-300',
        )}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Upload className={cn('w-10 h-10 mx-auto mb-3', dragActive ? 'text-primary-500' : 'text-gray-400')} />
        <p className="text-sm font-medium text-gray-700 mb-1">
          Перетащите файлы сюда
        </p>
        <p className="text-xs text-gray-500 mb-4">
          XLSX (консолидация) и PDF (накладные)
        </p>
        <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors">
          <Upload className="w-4 h-4" />
          Выбрать файлы
          <input
            type="file"
            accept=".xlsx,.pdf"
            multiple
            className="hidden"
            onChange={(e) => processFiles(e.target.files)}
          />
        </label>
      </div>

      {(xlsxFile || pdfFiles.length > 0) && (
        <div className="space-y-2">
          {xlsxFile && (
            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600 shrink-0" />
              <span className="text-sm font-medium text-emerald-800 truncate flex-1">{xlsxFile.name}</span>
              <button onClick={() => setXlsxFile(null)} className="text-emerald-400 hover:text-emerald-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {pdfFiles.map((pdf, index) => (
            <div key={`${pdf.name}-${index}`} className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
              <FileText className="w-5 h-5 text-blue-600 shrink-0" />
              <span className="text-sm font-medium text-blue-800 truncate flex-1">{pdf.name}</span>
              <button onClick={() => setPdfFiles(prev => prev.filter((_, i) => i !== index))} className="text-blue-400 hover:text-blue-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}

          {!xlsxFile && (
            <p className="text-xs text-amber-600 font-medium">Добавьте XLSX файл консолидации</p>
          )}
          {pdfFiles.length === 0 && (
            <p className="text-xs text-amber-600 font-medium">Добавьте PDF файл(ы) накладных</p>
          )}

          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={cn(
              'w-full py-3 rounded-xl text-sm font-medium transition-all duration-200',
              canSubmit
                ? 'bg-primary-600 text-white hover:bg-primary-700 active:scale-[0.98]'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed',
            )}
          >
            {isUploading ? 'Загрузка...' : 'Загрузить файлы'}
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;
